/**
 * MenuItem Component
 *
 * A navigation menu item with icon and label.
 * Used in sidebar navigation and app bar menus.
 *
 * Features:
 * - Icon + label layout
 * - Disabled state with reduced opacity
 * - Hidden state
 * - Hover animations (scale + brightness)
 * - Keyboard accessible
 *
 * Ported from Vue MenuItem.vue
 *
 * @example
 * ```tsx
 * <MenuItem
 *   icon="calendar"
 *   label="Schedule"
 *   onClick={() => navigate('/schedule')}
 *   data-testid="nav-schedule"
 * />
 * ```
 */

import React from "react";
import { Icon } from "@/components/ui/display/Icon";
import { cx } from "@/utils/cx";
import type { MenuItemProps } from "./types";
import styles from "./MenuItem.module.css";

export const MenuItem = React.forwardRef<HTMLDivElement, MenuItemProps>(
  (
    {
      label,
      icon,
      disabled = false,
      hidden = false,
      onClick,
      className = "",
      "data-testid": testId,
    },
    ref,
  ) => {
    // Handle click events
    const handleClick = () => {
      if (!disabled && onClick) {
        onClick();
      }
    };

    // Handle keyboard events for accessibility
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (disabled) return;

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick?.();
      }
    };

    // Build class names
    const containerClasses = cx(
      styles.container,
      disabled && styles.disabled,
      hidden && styles.hidden,
      className,
    );

    if (hidden) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={containerClasses}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="menuitem"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        data-testid={testId}
      >
        <Icon
          name={icon}
          size="md"
          className={styles.icon}
          aria-hidden={true}
        />
        {label && <span className={styles.label}>{label}</span>}
      </div>
    );
  },
);

MenuItem.displayName = "MenuItem";
